<script setup lang="ts">
/**
 * La ficha de un cliente, completa (§56).
 *
 * Cierra el pendiente de §49.2: sus datos, sus actividades, sus presupuestos,
 * sus recibos, su declaración médica, sus adjuntos y una bitácora. Antes eso
 * estaba repartido entre un modal de edición, otro modal de actividades y dos
 * listados de Sales, y había que saber de antemano dónde buscar cada cosa.
 *
 * El formato es el **`user view` de la plantilla**, con sus piezas: la cabecera
 * de `UserBioPanel.vue` (avatar `rounded`, nombre en `text-h5 mt-4`, chip
 * debajo), su bloque `Details` con `VList class="card-list"`, las pestañas
 * `v-tabs-pill` con ícono, y el `VWindow` con `disable-tab-transition` y
 * `:touch="false"` — igual que Settings › Profile y Company (§20.14).
 *
 * Dos desvíos, y los dos por lo mismo: lo que se viene a leer son los datos. El
 * avatar va **más chico** que los 120 de la demo, y los contadores de tareas y
 * proyectos no van — no tienen equivalente acá, y vacíos ocupan el lugar de lo
 * que sí importa.
 */
import {
  type ClientPayload,
  addClientNote,
  cancelClientLinkRequest,
  deleteClient,
  deleteClientDocument,
  deleteClientNote,
  getClient,
  getClientSales,
  inviteClientToApp,
  updateClient,
  uploadClientDocument,
} from '@/api/clients'
import { listDiveCenters } from '@/api/dive-centers'
import {
  type MedicalForm,
  createBlankMedicalFormPdfLink,
  createMedicalFormPdfLink,
  listClientMedicalForms,
  submitClientMedicalForm,
} from '@/api/medical'
import { ApiError, apiOrigin } from '@/api/client'
import type { Quote, QuoteDisplayStatus } from '@/api/quotes'
import type { Receipt } from '@/api/receipts'
import AlertBox from '@/components/AlertBox.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ClientActivitiesPanel from '@/components/ClientActivitiesPanel.vue'
import ClientEditorModal, { type ClientEditorExtras } from '@/components/ClientEditorModal.vue'
import LinkedDiverPanel from '@/components/LinkedDiverPanel.vue'
import RequestClientLinkDialog from '@/components/RequestClientLinkDialog.vue'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'
import { INTAKE_LOCALES } from '@/data/intakeI18n'
import type { DiveCenter, DiveCenterClient } from '@/types'

definePage({
  meta: {
    requiresAuth: true,

    // Sin item propio en el menú: se marca Clients.
    navActiveLink: 'clients',
  },
})

const route = useRoute('clients-client-id')
const router = useRouter()
const { currentId } = useCurrentDiveCenter()
const { formatCurrency, formatDate } = usePreferences()

const clientId = computed(() => String(route.params.clientId))

const client = ref<DiveCenterClient | null>(null)
const diveCenters = ref<DiveCenter[]>([])
const receipts = ref<Receipt[]>([])

/**
 * Las declaraciones que recogió el CENTRO, de la más nueva hacia atrás.
 *
 * Es otra cosa que la del buzo (`linkedProfile.medical`): aquella la firmó en su
 * app y llega por referencia; esta la cargó el mostrador y es del centro. La
 * pestaña mostraba solo la primera, así que una ficha con su declaración en papel
 * se veía vacía.
 */
const medicalForms = ref<MedicalForm[]>([])

const latestMedicalForm = computed(() => medicalForms.value[0])
const quotes = ref<(Quote & { displayStatus: QuoteDisplayStatus })[]>([])

const loading = ref(true)
const error = ref('')

/**
 * Las pestañas, con su ícono. Por índice y no por nombre, que es como las maneja
 * la plantilla: el `VWindow` empareja por posición con los `VWindowItem`.
 */
const TABS = [
  { icon: 'ri-calendar-check-line', title: 'Activities' },
  { icon: 'ri-receipt-line', title: 'Receipts' },
  { icon: 'ri-file-list-3-line', title: 'Quotes' },
  { icon: 'ri-heart-pulse-line', title: 'Medical' },
  { icon: 'ri-folder-line', title: 'Documents' },
] as const

const tab = ref(0)

const confirmRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)

const showEditor = ref(false)

/** Con qué paso abrir el editor. La pestaña Medical entra por el suyo. */
const editorStep = ref<'personal' | 'medical' | 'documents' | undefined>(undefined)
const submitting = ref(false)
const editorError = ref('')
const showLinkRequest = ref(false)
const inviting = ref(false)
const cancelling = ref(false)
const pdfLoading = ref(false)

/** Con vínculo aceptado manda el nombre del buzo; sin él, el que cargó el centro. */
const fullName = computed(() => {
  const c = client.value

  if (!c)
    return ''

  const diver = c.linkedProfile?.diver

  if (diver?.firstName || diver?.nickName) {
    return [diver.firstName, diver.nickName ? `@${diver.nickName}` : '']
      .filter(Boolean).join(' ').trim()
  }

  return `${c.firstName} ${c.lastName ?? ''}`.trim()
})

const linkChip = computed(() => {
  const s = client.value?.linkStatus

  if (s === 'linked')
    return { color: 'success', label: 'Linked' }
  if (s === 'pending')
    return { color: 'warning', label: 'Awaiting response' }
  if (s === 'rejected')
    return { color: 'error', label: 'Rejected' }
  if (s === 'revoked')
    return { color: 'error', label: 'Revoked' }

  return { color: 'secondary', label: 'Not linked' }
})

/**
 * ¿Hay declaración para imprimir?
 *
 * Una ficha sin nada trae el campo **ausente**, no `'none'`, así que preguntar
 * por el valor dejaba el botón encendido contra un PDF que no existe.
 */
const hasMedical = computed(() =>
  !!client.value?.medicalStatus && client.value.medicalStatus !== 'none',
)

/**
 * ¿Se puede cargar o corregir el cuestionario?
 *
 * Solo mientras no haya una firmada y vigente: una declaración firmada no se
 * edita —el propio paso del wizard la muestra bloqueada— así que ofrecer el botón
 * lleva a una pantalla que no deja hacer nada. Vencida o sin firmar, sí.
 */
const canFillMedical = computed(() => {
  const s = client.value?.medicalStatus

  return !s || s === 'none' || s === 'draft' || s === 'expired'
})

function openMedicalEditor() {
  editorStep.value = 'medical'
  showEditor.value = true
}

const medicalChip = computed(() => {
  const s = client.value?.medicalStatus

  if (s === 'signed')
    return { color: 'success', label: 'Completed' }
  if (s === 'expired')
    return { color: 'warning', label: 'Expired' }
  if (s === 'draft')
    return { color: 'info', label: 'Unsigned' }

  return { color: 'secondary', label: 'Missing' }
})

const centerNames = computed(() => {
  const byId = new Map(diveCenters.value.map(dc => [dc.id, dc.name]))

  return (client.value?.diveCenterIds ?? []).map(id => byId.get(id)).filter(Boolean) as string[]
})

/**
 * Los datos de la ficha, como pares rótulo/valor.
 *
 * Se arma en el script y no en la plantilla para que las filas vacías
 * desaparezcan solas: una lista de "—" no dice nada que no diga no estar.
 */
const details = computed(() => {
  const c = client.value

  if (!c)
    return []

  const rows: { label: string; value: string }[] = []

  const push = (label: string, value?: string | number | null) => {
    if (value != null && String(value).trim())
      rows.push({ label, value: String(value) })
  }

  push('Email', c.email)
  push('Phone', c.phone)
  push('Date of birth', c.birthDate ? formatDate(c.birthDate) : '')
  push(
    'Certification',
    [c.certificationLevel, c.certificationAgency].filter(Boolean).join(' · '),
  )
  push('Logged dives', c.loggedDives)
  push('Dive centers', centerNames.value.join(', '))
  push('Client since', c.createdAt ? formatDate(c.createdAt) : '')

  return rows
})

/**
 * A qué centro llevan los atajos que necesitan uno.
 *
 * El que se esté usando, si la ficha opera ahí; si no, el primero suyo. Una
 * ficha puede estar en varios y los recibos y el calendario cuelgan de uno.
 */
const targetCenterId = computed(() => {
  const own = client.value?.diveCenterIds ?? []

  if (currentId.value && own.includes(currentId.value))
    return currentId.value

  return own[0] ?? currentId.value ?? ''
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    /*
     * Los tres en paralelo: no dependen entre sí, y encadenarlos son tres
     * esperas para pintar una pantalla (§51.4). Los centros se piden acá porque
     * la ficha nombra los suyos y el editor necesita la lista.
     */
    const [res, sales, dcs, forms] = await Promise.all([
      getClient(clientId.value),
      getClientSales(clientId.value).catch(() => ({ receipts: [], quotes: [] })),
      listDiveCenters().catch(() => ({ diveCenters: [] })),
      listClientMedicalForms(clientId.value).catch(() => ({ forms: [] })),
    ])

    client.value = res.client
    receipts.value = sales.receipts
    quotes.value = sales.quotes
    diveCenters.value = dcs.diveCenters
    medicalForms.value = forms.forms
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not load this client.'
  }
  finally {
    loading.value = false
  }
}

onMounted(async () => {
  await load()

  /*
   * `?edit=1` abre el editor de una. Es la puerta desde el roster: mirando quién
   * viene a una salida se detecta que a alguien le falta la certificación, y
   * corregirlo tiene que ser un click y no "abrir la ficha y buscar el botón".
   */
  if (route.query.edit)
    showEditor.value = true
})

// ---- Acciones de la tarjeta ----

async function invite() {
  const c = client.value

  if (!c?.email)
    return

  const ok = await confirmRef.value?.open({
    title: 'Invite to Blue Trails',
    message: `Email ${c.email} an invitation to download the app? They stay your client either way — this is just a recommendation.`,
    confirmText: 'Send invitation',
    confirmColor: 'primary',
  })

  if (!ok)
    return

  inviting.value = true
  try {
    const res = await inviteClientToApp(c.id)

    client.value = res.client
    notifySuccess(`Invitation sent to ${c.email}.`)
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not send the invitation.'))
  }
  finally {
    inviting.value = false
  }
}

async function cancelLink() {
  if (!client.value)
    return

  cancelling.value = true
  try {
    const res = await cancelClientLinkRequest(client.value.id)

    client.value = res.client
    notifySuccess('Request withdrawn.')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not withdraw the request.'))
  }
  finally {
    cancelling.value = false
  }
}

async function remove() {
  if (!client.value)
    return

  const ok = await confirmRef.value?.open({
    title: 'Delete client',
    message: `Delete ${fullName.value} from your client book? This erases their personal details and cannot be undone.`,
    confirmText: 'Delete',
    confirmColor: 'error',
  })

  if (!ok)
    return

  try {
    await deleteClient(client.value.id)
    notifySuccess('Client deleted.')
    router.push('/clients')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not delete the client.'))
  }
}

// ---- Editor ----

async function submitEditor(payload: ClientPayload, extras: ClientEditorExtras) {
  if (!client.value)
    return

  editorError.value = ''
  submitting.value = true
  try {
    const saved = (await updateClient(client.value.id, payload)).client

    /*
     * La declaración va DESPUÉS del guardado, como en el alta: si falla, la
     * ficha ya quedó guardada y se avisa qué no entró en vez de deshacer todo.
     */
    if (extras.medical) {
      try {
        await submitClientMedicalForm(saved.id, extras.medical)
      }
      catch (e) {
        notifyError(apiMessage(e, 'The client was saved, but the medical form was not.'))
      }
    }

    showEditor.value = false
    notifySuccess('Client updated.')
    await load()
  }
  catch (e) {
    editorError.value = e instanceof ApiError ? e.message : 'Could not save the client.'
  }
  finally {
    submitting.value = false
  }
}

// ---- Documentos ----

const uploading = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

async function onFiles(event: Event) {
  const input = event.target as HTMLInputElement
  const files = [...(input.files ?? [])]

  input.value = ''

  if (!files.length || !client.value)
    return

  uploading.value = true
  try {
    /*
     * En serie y no en paralelo: el backend valida el tope de adjuntos contra el
     * estado actual, y en paralelo dos subidas leerían el mismo conteo y lo
     * pasarían. Es una de las excepciones escritas de §51.4.
     */
    for (const file of files) {
      try {
        const res = await uploadClientDocument(client.value.id, file)

        client.value = res.client
      }
      catch (e) {
        notifyError(apiMessage(e, `Could not upload ${file.name}.`))
      }
    }
  }
  finally {
    uploading.value = false
  }
}

async function removeDocument(index: number) {
  if (!client.value)
    return

  const ok = await confirmRef.value?.open({
    title: 'Remove document',
    message: 'This deletes the file from the client’s record. It cannot be undone.',
    confirmText: 'Remove',
    confirmColor: 'error',
  })

  if (!ok)
    return

  try {
    const res = await deleteClientDocument(client.value.id, index)

    client.value = res.client
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not remove the document.'))
  }
}

// ---- Bitácora ----

const noteDraft = ref('')
const savingNote = ref(false)

/**
 * La bitácora vive en un **drawer**, no en una pestaña.
 *
 * Anotar algo casi siempre es anotar algo que se está mirando —"el jueves vino
 * sin carnet"— y una pestaña tapa justamente eso. Desde el costado, las
 * actividades quedan a la vista mientras se escribe.
 */
const notesOpen = ref(false)

const noteCount = computed(() => client.value?.noteEntries?.length ?? 0)

async function addNote() {
  const text = noteDraft.value.trim()

  if (!text || !client.value)
    return

  savingNote.value = true
  try {
    const res = await addClientNote(client.value.id, text)

    client.value = res.client
    noteDraft.value = ''
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not save the note.'))
  }
  finally {
    savingNote.value = false
  }
}

async function removeNote(noteId: string) {
  if (!client.value)
    return

  try {
    const res = await deleteClientNote(client.value.id, noteId)

    client.value = res.client
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not remove the note.'))
  }
}

// ---- PDFs médicos ----

/**
 * 🪤 La pestaña se abre **antes** del `await`: un `window.open` posterior a una
 * espera ya no cuenta como gesto del usuario y lo mata el bloqueador (§21.7).
 */
async function openPdf(build: () => Promise<{ url: string }>) {
  const tab_ = window.open('', '_blank')

  pdfLoading.value = true
  try {
    const { url } = await build()
    const absolute = new URL(url, apiOrigin()).toString()

    if (tab_)
      tab_.location.href = absolute
    else window.open(absolute, '_blank')
  }
  catch (e) {
    tab_?.close()
    notifyError(apiMessage(e, 'Could not open the PDF.'))
  }
  finally {
    pdfLoading.value = false
  }
}

const openCenterMedicalPdf = () =>
  openPdf(() => createMedicalFormPdfLink(clientId.value, {}))

const openDiverMedicalPdf = (locale: string) =>
  openPdf(() => createMedicalFormPdfLink(clientId.value, { source: 'diver', locale }))

const openBlankMedicalPdf = (locale: string) =>
  openPdf(() => createBlankMedicalFormPdfLink(locale))

// ---- Papeles ----

const RECEIPT_CHIP: Record<string, { color: string; label: string }> = {
  draft: { color: 'secondary', label: 'Draft' },
  paid: { color: 'success', label: 'Paid' },
  void: { color: 'error', label: 'Void' },
}

const QUOTE_CHIP: Record<string, { color: string; label: string }> = {
  draft: { color: 'secondary', label: 'Draft' },
  sent: { color: 'info', label: 'Sent' },
  approved: { color: 'success', label: 'Approved' },
  rejected: { color: 'error', label: 'Rejected' },
  expired: { color: 'warning', label: 'Expired' },
  converted: { color: '#7E57C2', label: 'Converted' },
  partially_converted: { color: 'warning', label: 'Partly converted' },
}
</script>

<template>
  <div>
    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
    />

    <AlertBox
      variant="danger"
      :message="error"
    />

    <!--
      La vuelta al listado. Sin esto la ficha es una calle sin salida: se entra
      desde la tabla y el único camino de vuelta es el botón del navegador.
      Mismo enlace que el preview de recibos y presupuestos.
    -->
    <div
      v-if="client"
      class="d-flex align-center mb-4"
    >
      <RouterLink
        to="/clients"
        class="d-flex align-center"
      >
        <VIcon
          icon="ri-arrow-left-line"
          size="18"
          class="me-1"
        />
        All clients
      </RouterLink>
    </div>

    <VRow v-if="client">
      <!-- Identidad y acciones. Los anchos son los de la plantilla. -->
      <VCol
        cols="12"
        md="5"
        lg="4"
      >
        <VCard>
          <!-- 👉 Cabecera de `UserBioPanel.vue`: avatar, nombre y chip. -->
          <VCardText class="text-center pt-8 pb-4">
            <!--
              Más chico que los 120 de la demo: allá arriba no hay nada más, y
              acá lo que se viene a leer son los datos.
            -->
            <!--
              🪤 La foto va en el SLOT y no en `:image`. `VAvatar` renderiza el
              slot por defecto **o** la imagen, nunca los dos: con un slot puesto
              —aunque su contenido esté apagado por un `v-if`— ignora `image` y el
              avatar sale vacío. Es lo que pasó al portar esto a la plantilla.
            -->
            <VAvatar
              rounded
              :size="88"
              variant="tonal"
              color="primary"
            >
              <VImg
                v-if="client.linkedProfile?.diver?.profileImageUrl"
                :src="client.linkedProfile.diver.profileImageUrl"
              />
              <span
                v-else
                class="text-h5"
              >{{ avatarText(fullName) }}</span>
            </VAvatar>

            <h5 class="text-h5 mt-4">
              {{ fullName }}
            </h5>

            <VChip
              :color="linkChip.color"
              size="small"
              class="mt-4"
            >
              {{ linkChip.label }}
            </VChip>
          </VCardText>

          <!-- 👉 `Details` de `UserBioPanel.vue`, pegado al nombre. -->
          <VCardText>
            <h5 class="text-h5">
              Details
            </h5>
            <VDivider class="my-4" />

            <VList class="card-list mt-2">
              <VListItem
                v-for="row in details"
                :key="row.label"
              >
                <VListItemTitle>
                  <span class="font-weight-medium">{{ row.label }}:</span>
                  <span class="text-body-1"> {{ row.value }}</span>
                </VListItemTitle>
              </VListItem>

              <!--
                La nota permanente de la ficha, no la bitácora: es lo que hay que
                saber de esta persona, y por eso va con sus datos y no en la
                pestaña de lo que fue pasando. Se edita en el modal.
              -->
              <VListItem v-if="client.notes">
                <VListItemTitle class="text-wrap">
                  <span class="font-weight-medium">Notes:</span>
                  <span
                    class="text-body-1"
                    style="white-space: pre-line"
                  > {{ client.notes }}</span>
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>

          <VCardText class="d-flex flex-column ga-3">
            <!--
              Invitar primero: es lo que más se hace con una ficha que todavía no
              tiene cuenta. Sin mail no hay a dónde mandarla, y a una ya vinculada
              ofrecerle "descargá la app" no tiene sentido.
            -->
            <VBtn
              block
              prepend-icon="ri-mail-send-line"
              :disabled="!client.email || client.linkStatus === 'linked'"
              :loading="inviting"
              @click="invite"
            >
              Invite to Blue Trails
            </VBtn>

            <!--
              Lo que se le crea a un cliente, en chips: son atajos y no acciones
              sobre la ficha, así que no compiten en peso con los botones de
              arriba. Con el nombre a la vista y no solo el ícono — tres círculos
              iguales obligaban a pasar el mouse por cada uno para saber cuál era.
            -->
            <div class="d-flex flex-wrap justify-center ga-2">
              <VChip
                link
                size="small"
                color="primary"
                prepend-icon="ri-receipt-line"
                :to="`/sales/receipts/new?clientId=${client.id}`"
              >
                New receipt
              </VChip>

              <VChip
                link
                size="small"
                color="primary"
                prepend-icon="ri-file-list-3-line"
                :to="`/sales/quotes/new?clientId=${client.id}`"
              >
                New quote
              </VChip>

              <!--
                Anotarlo en una salida se sigue haciendo desde el calendario, que
                es donde se ve la disponibilidad: el chip lleva ahí en vez de
                inventar un segundo lugar donde elegir salidas.
              -->
              <VChip
                link
                size="small"
                color="primary"
                prepend-icon="ri-calendar-2-line"
                :disabled="!targetCenterId"
                :to="`/dive-centers/${targetCenterId}/calendar`"
              >
                New activity
              </VChip>

              <!--
                La bitácora entra por acá y se abre de costado. El contador la
                convierte además en la puerta para LEERLAS: sin él, "New note" no
                dice que hay tres esperando.
              -->
              <!--
                El contador va en un `VBadge` y no entre paréntesis: cuelga del
                chip sin robarle ancho, que es lo que hace que los cuatro sigan
                entrando en la misma fila. `model-value` lo apaga en cero — un
                globo con un 0 dice que hay algo y no hay nada.

                🪤 Se probó `inline` y achicado: `inline` lo saca de la esquina y
                lo mete en el flujo, así que empuja el chip y rompe la fila.
              -->
              <VBadge
                :content="noteCount"
                :model-value="noteCount > 0"
                color="secondary"
              >
                <VChip
                  link
                  size="small"
                  color="primary"
                  prepend-icon="ri-sticky-note-line"
                  @click="notesOpen = true"
                >
                  New note
                </VChip>
              </VBadge>
            </div>

            <!--
              Pedir el vínculo. Sale de una ficha que YA existe a propósito: si un
              acierto creara una fila y un fallo no, la fila misma delataría quién
              tiene cuenta en Blue Trails.
            -->
            <VBtn
              v-if="client.linkStatus !== 'linked' && client.linkStatus !== 'pending'"
              block
              variant="outlined"
              prepend-icon="ri-link"
              @click="showLinkRequest = true"
            >
              {{ client.linkStatus === 'rejected' ? 'Ask again' : 'Link a Blue Trails account' }}
            </VBtn>

            <VBtn
              v-else-if="client.linkStatus === 'pending'"
              block
              variant="outlined"
              prepend-icon="ri-link-unlink"
              :loading="cancelling"
              @click="cancelLink"
            >
              Cancel link request
            </VBtn>

            <div class="d-flex ga-2">
              <VBtn
                variant="tonal"
                class="flex-grow-1"
                prepend-icon="ri-edit-box-line"
                @click="editorStep = undefined; showEditor = true"
              >
                Edit
              </VBtn>
              <VBtn
                variant="tonal"
                color="error"
                class="flex-grow-1"
                prepend-icon="ri-delete-bin-7-line"
                @click="remove"
              >
                Delete
              </VBtn>
            </div>
          </VCardText>
        </VCard>

        <!--
          Lo que aporta la cuenta vinculada, tal como se muestra en el editor: es
          de solo lectura y llega por referencia, así que si el buzo revoca el
          bloque desaparece solo. El médico va en su pestaña.

          🪤 **Suelto y no dentro de un `VCard`.** El panel ya ES una tarjeta
          (`VCard variant="tonal"`), y envolverlo dejaba una tarjeta dentro de
          otra: doble borde, doble padding, y el contenido estrujado contra el
          ancho de la columna.
        -->
        <LinkedDiverPanel
          v-if="client.linkedProfile"
          :profile="client.linkedProfile"
          hide-medical
          class="mt-6"
        />
      </VCol>

      <VCol
        cols="12"
        md="7"
        lg="8"
      >
        <!-- `v-tabs-pill` con ícono, como Settings › Profile y Company. -->
        <VTabs
          v-model="tab"
          class="v-tabs-pill"
        >
          <VTab
            v-for="(t, i) in TABS"
            :key="t.icon"
          >
            <VIcon
              start
              :icon="t.icon"
            />
            <span>{{ t.title }}</span>
            <!-- Cuántos papeles hay, sin abrir la pestaña. -->
            <VChip
              v-if="i === 1 && receipts.length"
              size="x-small"
              class="ms-2"
            >
              {{ receipts.length }}
            </VChip>
            <VChip
              v-if="i === 2 && quotes.length"
              size="x-small"
              class="ms-2"
            >
              {{ quotes.length }}
            </VChip>
          </VTab>
        </VTabs>

        <VWindow
          v-model="tab"
          class="mt-6 disable-tab-transition"
          :touch="false"
        >
          <VWindowItem>
            <VCard title="Activities">
              <VCardText>
                <ClientActivitiesPanel
                  :client="client"
                  @changed="load"
                />
              </VCardText>
            </VCard>
          </VWindowItem>

          <VWindowItem>
            <VCard title="Receipts">
              <VCardText>
                <p
                  v-if="!receipts.length"
                  class="text-body-2 text-medium-emphasis mb-0"
                >
                  No receipts for this client yet.
                </p>

                <VTable
                  v-else
                  class="text-no-wrap"
                >
                  <thead>
                    <tr>
                      <th>Receipt</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th class="text-end">
                        Total
                      </th>
                      <th style="inline-size: 48px" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in receipts"
                      :key="r.id"
                    >
                      <td>{{ r.number ?? 'Draft' }}</td>
                      <td>{{ formatDate(r.paidAt ?? r.createdAt) }}</td>
                      <td>
                        <VChip
                          size="small"
                          :color="RECEIPT_CHIP[r.status]?.color ?? 'secondary'"
                        >
                          {{ RECEIPT_CHIP[r.status]?.label ?? r.status }}
                        </VChip>
                      </td>
                      <td class="text-end font-weight-medium">
                        {{ formatCurrency(r.total, r.currency) }}
                      </td>
                      <td>
                        <IconBtn
                          size="small"
                          :to="`/sales/receipts/${r.id}/preview`"
                        >
                          <VIcon icon="ri-eye-line" />
                        </IconBtn>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </VCardText>
            </VCard>
          </VWindowItem>

          <VWindowItem>
            <VCard title="Quotes">
              <VCardText>
                <p
                  v-if="!quotes.length"
                  class="text-body-2 text-medium-emphasis mb-0"
                >
                  No quotes for this client yet.
                </p>

                <VTable
                  v-else
                  class="text-no-wrap"
                >
                  <thead>
                    <tr>
                      <th>Quote</th>
                      <th>Date</th>
                      <th>Valid until</th>
                      <th>Status</th>
                      <th class="text-end">
                        Total
                      </th>
                      <th style="inline-size: 48px" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="q in quotes"
                      :key="q.id"
                    >
                      <td>{{ q.number ?? 'Draft' }}</td>
                      <td>{{ formatDate(q.createdAt) }}</td>
                      <td>{{ q.validUntil ? formatDate(q.validUntil) : '—' }}</td>
                      <td>
                        <VChip
                          size="small"
                          :color="QUOTE_CHIP[q.displayStatus]?.color ?? 'secondary'"
                        >
                          {{ QUOTE_CHIP[q.displayStatus]?.label ?? q.displayStatus }}
                        </VChip>
                      </td>
                      <td class="text-end font-weight-medium">
                        {{ formatCurrency(q.total, q.currency) }}
                      </td>
                      <td>
                        <IconBtn
                          size="small"
                          :to="`/sales/quotes/${q.id}/preview`"
                        >
                          <VIcon icon="ri-eye-line" />
                        </IconBtn>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </VCardText>
            </VCard>
          </VWindowItem>

          <!--
            Se consulta y se imprime; cargar o corregir el cuestionario sigue en
            el wizard del editor, que es donde están sus validaciones. Dos
            formularios sobre el mismo dato terminan divergiendo.
          -->
          <VWindowItem>
            <VCard title="Medical declaration">
              <VCardText>
                <div class="d-flex flex-wrap align-center ga-3 mb-4">
                  <VChip
                    :color="medicalChip.color"
                    size="small"
                  >
                    {{ medicalChip.label }}
                  </VChip>
                  <span
                    v-if="client.medicalSubmittedAt"
                    class="text-body-2 text-medium-emphasis"
                  >Submitted {{ formatDate(client.medicalSubmittedAt) }}</span>

                  <VSpacer />

                  <!--
                    Llenarla es lo primero: sin declaración cargada no hay nada
                    que imprimir, y el papel en blanco ya tiene su propio botón.
                    Abre el editor DIRECTO en su paso — arrancar en "Personal
                    details" obliga a avanzar cuatro para llegar a lo que se vino
                    a hacer.
                  -->
                  <VBtn
                    v-if="canFillMedical"
                    size="small"
                    prepend-icon="ri-edit-box-line"
                    @click="openMedicalEditor"
                  >
                    {{ hasMedical ? 'Complete questionnaire' : 'Fill in questionnaire' }}
                  </VBtn>

                  <!--
                    🪤 Se apagaba con `=== 'none'`, y una ficha sin declaración
                    trae el campo **ausente**: el botón quedaba encendido y abría
                    una pestaña contra un PDF que no existe.
                  -->
                  <VBtn
                    variant="tonal"
                    size="small"
                    prepend-icon="ri-printer-line"
                    :loading="pdfLoading"
                    :disabled="!hasMedical"
                    @click="openCenterMedicalPdf"
                  >
                    Print declaration
                  </VBtn>

                  <!--
                    Los nueve idiomas para la hoja en blanco: todavía no la leyó
                    nadie, así que se imprime en el idioma de quien la completa.
                  -->
                  <VBtn
                    variant="tonal"
                    size="small"
                    prepend-icon="ri-file-text-line"
                    append-icon="ri-arrow-down-s-line"
                    :loading="pdfLoading"
                  >
                    Blank form

                    <VMenu activator="parent">
                      <VList
                        density="compact"
                        max-height="360"
                      >
                        <VListItem
                          v-for="l in INTAKE_LOCALES"
                          :key="l.code"
                          @click="openBlankMedicalPdf(l.code)"
                        >
                          <VListItemTitle>{{ l.label }}</VListItemTitle>
                        </VListItem>
                      </VList>
                    </VMenu>
                  </VBtn>
                </div>

                <VDivider class="mb-4" />

                <!-- La que el buzo firmó en su app, si la compartió. -->
                <LinkedDiverPanel
                  v-if="client.linkedProfile?.medical"
                  :profile="client.linkedProfile"
                  medical-only
                  @open-diver-pdf="openDiverMedicalPdf"
                />

                <!--
                  Y la que recogió el CENTRO, que es la otra mitad y no se veía:
                  una ficha con su declaración en papel salía como si no tuviera
                  nada. Son dos documentos distintos y los dos cuentan.
                -->
                <template v-if="latestMedicalForm">
                  <div class="d-flex flex-wrap align-center ga-2 mb-3">
                    <h6 class="text-h6 mb-0">
                      Collected by your team
                    </h6>
                    <span class="text-body-2 text-medium-emphasis">
                      {{ latestMedicalForm.source === 'public_form' ? 'from the counter QR' : '' }}
                      · {{ formatDate(latestMedicalForm.submittedAt) }}
                    </span>
                    <VChip
                      v-if="latestMedicalForm.expired"
                      size="small"
                      color="warning"
                    >
                      Expired
                    </VChip>
                    <VChip
                      v-else-if="!latestMedicalForm.declarationAccepted"
                      size="small"
                      color="info"
                    >
                      Unsigned
                    </VChip>
                  </div>

                  <!--
                    🪤 El chip con `flex-shrink-0` y el enunciado con
                    `min-inline-size: 0`: sin las dos, una pregunta larga estruja
                    el "No" hasta dejarlo ilegible.
                  -->
                  <div
                    v-for="(a, i) in latestMedicalForm.answers"
                    :key="a.questionCode"
                    class="d-flex align-center ga-4 py-3"
                    :class="i ? 'border-t' : ''"
                  >
                    <p
                      class="text-body-2 mb-0 flex-grow-1"
                      style="min-inline-size: 0"
                    >
                      {{ a.questionText }}
                    </p>
                    <VChip
                      size="small"
                      class="flex-shrink-0"
                      :color="a.answer === true ? 'warning' : a.answer === false ? 'secondary' : undefined"
                    >
                      {{ a.answer === true ? 'Yes' : a.answer === false ? 'No' : '—' }}
                    </VChip>
                  </div>

                  <template v-if="latestMedicalForm.declarationAccepted">
                    <VDivider class="my-4" />
                    <div class="text-body-2">
                      Signed by
                      <span class="font-weight-medium">{{ latestMedicalForm.declaredName || '—' }}</span>
                    </div>
                    <VImg
                      v-if="latestMedicalForm.signatureUrl"
                      :src="latestMedicalForm.signatureUrl"
                      :max-width="280"
                      class="border rounded mt-2"
                    />
                  </template>
                </template>

                <p
                  v-else-if="!client.linkedProfile?.medical"
                  class="text-body-2 text-medium-emphasis mb-0"
                >
                  Nothing on file. Fill in the questionnaire, or print the blank form
                  for the diver to complete.
                </p>
              </VCardText>
            </VCard>
          </VWindowItem>

          <!--
            Documents sí opera: subir un carnet no puede obligar a abrir el
            wizard y avanzar hasta su paso.
          -->
          <VWindowItem>
            <VCard title="Documents">
              <VCardText>
                <div class="d-flex align-center mb-4">
                  <span class="text-body-2 text-medium-emphasis">
                    Card, insurance, a signed form on paper.
                  </span>
                  <VSpacer />
                  <VBtn
                    size="small"
                    prepend-icon="ri-upload-2-line"
                    :loading="uploading"
                    @click="fileInput?.click()"
                  >
                    Upload
                  </VBtn>
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    hidden
                    @change="onFiles"
                  >
                </div>

                <p
                  v-if="!client.documents?.length"
                  class="text-body-2 text-medium-emphasis mb-0"
                >
                  Nothing on file yet.
                </p>

                <VList
                  v-else
                  class="py-0"
                >
                  <VListItem
                    v-for="(doc, i) in client.documents"
                    :key="doc.url"
                  >
                    <template #prepend>
                      <VAvatar
                        size="32"
                        rounded
                        variant="tonal"
                        color="secondary"
                      >
                        <VIcon
                          icon="ri-attachment-2"
                          size="18"
                        />
                      </VAvatar>
                    </template>

                    <VListItemTitle>
                      <a
                        :href="doc.url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >{{ doc.name }}</a>
                    </VListItemTitle>
                    <VListItemSubtitle v-if="doc.uploadedAt">
                      {{ formatDate(doc.uploadedAt) }}
                    </VListItemSubtitle>

                    <template #append>
                      <IconBtn
                        size="small"
                        color="error"
                        @click="removeDocument(i)"
                      >
                        <VIcon icon="ri-delete-bin-line" />
                      </IconBtn>
                    </template>
                  </VListItem>
                </VList>
              </VCardText>
            </VCard>
          </VWindowItem>
        </VWindow>
      </VCol>
    </VRow>

    <!--
      La bitácora, de costado (§56.6). Va en la RAÍZ de la página: un
      `VNavigationDrawer` dentro de un contexto de apilado propio queda recortado
      y con el velo puesto solo sobre él (§24.8).
    -->
    <VNavigationDrawer
      v-model="notesOpen"
      temporary
      location="end"
      :width="$vuetify.display.smAndDown ? undefined : 460"
      class="bt-drawer"
    >
      <div class="d-flex align-center justify-space-between pa-4">
        <div>
          <div class="text-h6">
            Notes
          </div>
          <div class="text-caption text-medium-emphasis">
            What happened with this diver, with who wrote it and when.
          </div>
        </div>
        <VBtn
          icon="ri-close-line"
          variant="text"
          size="small"
          @click="notesOpen = false"
        />
      </div>

      <VDivider />

      <div class="pa-4">
        <VTextarea
          v-model="noteDraft"
          label="Add a note"
          rows="2"
          auto-grow
          class="mb-3"
        />
        <div class="d-flex justify-end mb-4">
          <VBtn
            size="small"
            :disabled="!noteDraft.trim()"
            :loading="savingNote"
            @click="addNote"
          >
            Add note
          </VBtn>
        </div>

        <VDivider class="mb-4" />

        <p
          v-if="!noteCount"
          class="text-body-2 text-medium-emphasis mb-0"
        >
          No notes yet.
        </p>

        <div
          v-for="note in client?.noteEntries ?? []"
          :key="note.id"
          class="d-flex align-start ga-3 mb-4"
        >
          <div class="flex-grow-1">
            <div class="text-caption text-medium-emphasis">
              {{ note.authorName || 'Someone' }} · {{ formatDate(note.createdAt) }}
            </div>
            <p
              class="text-body-2 mb-0"
              style="white-space: pre-line"
            >
              {{ note.text }}
            </p>
          </div>

          <IconBtn
            size="small"
            color="error"
            @click="removeNote(note.id)"
          >
            <VIcon icon="ri-delete-bin-line" />
          </IconBtn>
        </div>
      </div>
    </VNavigationDrawer>

    <ClientEditorModal
      :show="showEditor"
      :client="client"
      :dive-centers="diveCenters"
      :initial-step="editorStep"
      :submitting="submitting"
      :error="editorError"
      @close="showEditor = false; editorStep = undefined"
      @submit="submitEditor"
      @upload-document="(f: File) => client && uploadClientDocument(client.id, f).then(r => (client = r.client))"
      @remove-document="removeDocument"
      @open-pdf="openCenterMedicalPdf"
      @open-diver-pdf="openDiverMedicalPdf"
    />

    <RequestClientLinkDialog
      :show="showLinkRequest"
      :client="client"
      :dive-centers="diveCenters"
      @close="showLinkRequest = false"
      @requested="load"
    />

    <ConfirmDialog ref="confirmRef" />
  </div>
</template>

<style lang="scss" scoped>
/* 🪤 `.v-navigation-drawer__content` viene con `overflow-y: hidden` (§20.7). */
.bt-drawer :deep(.v-navigation-drawer__content) {
  overflow-y: auto;
}

/* El interlineado de `card-list` que usa el `Details` de la plantilla. */
.card-list {
  --v-card-list-gap: 0.5rem;
}
</style>
